

import React, { useState, useEffect, useRef } from 'react';
import apiService from '../Service/apiService';
import webSocketService from '../Service/webSocketService';
import './PromotionDeta.css';

const PromotionDeta = ({ data, deviceInfo, onLogout }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [nextCampaign, setNextCampaign] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBackButton, setShowBackButton] = useState(false);
  const [lastGetRefreshTime, setLastGetRefreshTime] = useState(new Date());
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const nextVideoRef = useRef(null);
  const hideButtonTimerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const preloadTimerRef = useRef(null);
  const getIntervalRef = useRef(null); // For GET every 10 minutes
  const putIntervalRef = useRef(null); // For PUT every 20 seconds
  const initialGetDoneRef = useRef(false);

  // Request wake lock to prevent screen from sleeping
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator && document.visibilityState === 'visible') {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
        }
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (err) {
      console.error('❌ Failed to activate Wake Lock:', err);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (err) {
        console.error('❌ Failed to release Wake Lock:', err);
      }
    }
  };
  useEffect(() => {
    if (deviceInfo?.deviceId && deviceInfo?.password && !initialGetDoneRef.current) {
      initialGetDoneRef.current = true;
      //console.log('🔄 Page refresh detected - making GET API call');
      fetchCampaignData('page-refresh');
    }
  }, [deviceInfo?.deviceId, deviceInfo?.password]);

  // ==================== GET API FUNCTION ====================
  const fetchCampaignData = async (source = 'initial') => {
    if (!deviceInfo?.deviceId || !deviceInfo?.password) {
      //console.log('⏭️ Skipping GET - no device info');
      return;
    }

    try {
      //console.log(`📥 GET API called at: ${new Date().toLocaleTimeString()} (source: ${source})`);

      const currentCampaignId = currentCampaign?.campaignId;

      const freshData = await apiService.getScreenData(deviceInfo.deviceId, deviceInfo.password);

      if (freshData && freshData.campaigns) {
        const activeCampaigns = freshData.campaigns.filter(
          campaign => campaign.medium === "DIGITAL" && campaign.status === "ACTIVE"
        );

        //console.log(`📊 GET response: ${activeCampaigns.length} active campaigns`);
        setCampaigns(activeCampaigns);

        const newPlaylist = apiService.generatePlaylist(activeCampaigns);
        setPlaylist(newPlaylist);

        // Handle current campaign
        if (currentCampaignId) {
          const campaignStillExists = newPlaylist.some(c => c.campaignId === currentCampaignId);

          if (!campaignStillExists && newPlaylist.length > 0) {
            //console.log('🎯 Current campaign changed, switching to new campaign');
            setCurrentCampaign(newPlaylist[0]);
            preloadNextCampaign(1, newPlaylist);
          } else if (campaignStillExists) {
            const updatedCurrentCampaign = newPlaylist.find(c => c.campaignId === currentCampaignId);
            setCurrentCampaign(updatedCurrentCampaign);
          }
        } else if (newPlaylist.length > 0) {
          setCurrentCampaign(newPlaylist[0]);
          preloadNextCampaign(1, newPlaylist);
        }

        setLastGetRefreshTime(new Date());
      }
    } catch (error) {
      console.error('❌ GET API failed:', error);
    }
  };

  // ==================== PUT API FUNCTION ====================
  const updateCampaignActive = async () => {
    if (!currentCampaign || !deviceInfo?.deviceId) {
      //console.log('⏭️ Skipping PUT - no active campaign');
      return;
    }

    try {
      //console.log(`📤 PUT API called at: ${new Date().toLocaleTimeString()} for campaign: ${currentCampaign.campaignId}`);

      await apiService.updateCampaignActive(
        currentCampaign.campaignId,
        deviceInfo.deviceId,
        true
      );

      //console.log(`✅ PUT successful for campaign: ${currentCampaign.campaignId}`);
    } catch (error) {
      console.error('❌ PUT API failed:', error);
    }
  };

  // ==================== TIMER SETUP ====================

  // 1. GET API every 10 minutes
  // Use useRef to store deviceId and password
  const deviceIdRef = useRef(deviceInfo?.deviceId);
  const passwordRef = useRef(deviceInfo?.password);

  // Update refs when deviceInfo changes
  useEffect(() => {
    if (deviceInfo?.deviceId) {
      deviceIdRef.current = deviceInfo.deviceId;
      passwordRef.current = deviceInfo.password;
    }
  }, [deviceInfo]);

  // Now use empty dependencies array - interval runs once
  useEffect(() => {
    if (!deviceIdRef.current || !passwordRef.current) return;

    //console.log('⏰ Setting up GET API interval (every 2 minutes)');

    // Clear any existing interval
    if (getIntervalRef.current) {
      clearInterval(getIntervalRef.current);
    }

    // Set up new interval
    getIntervalRef.current = setInterval(() => {
      // Use the ref values inside the interval
      if (deviceIdRef.current && passwordRef.current) {
        fetchCampaignData('10min-interval');
      }
    }, 600000);

    return () => {
      //console.log('🧹 Cleaning up GET interval');
      if (getIntervalRef.current) {
        clearInterval(getIntervalRef.current);
        getIntervalRef.current = null;
      }
    };
  }, []); // Empty array = runs only once

  // 2. PUT API every 20 seconds (EXACTLY 20 SECONDS)
  useEffect(() => {
    if (!currentCampaign || !deviceInfo?.deviceId) return;

    //console.log('⏰ Setting up PUT API interval (every 20 seconds)');

    // Clear any existing interval
    if (putIntervalRef.current) {
      clearInterval(putIntervalRef.current);
    }

    // Set up interval for PUT every 20 seconds
    putIntervalRef.current = setInterval(() => {
      updateCampaignActive();
    }, 20000); // 20 seconds = 20,000 ms

    return () => {
      //console.log('🧹 Cleaning up PUT interval');
      if (putIntervalRef.current) {
        clearInterval(putIntervalRef.current);
        putIntervalRef.current = null;
      }
    };
  }, [currentCampaign?.campaignId, deviceInfo?.deviceId]); // Re-run when campaign changes

  // 3. Initial GET on component mount (handles both login and page refresh)
  useEffect(() => {
    if (data && data.campaigns) {
      //console.log('📦 Initial GET from props (login/page refresh)');

      const activeCampaigns = data.campaigns.filter(
        campaign => campaign.medium === "DIGITAL" && campaign.status === "ACTIVE"
      );

      setCampaigns(activeCampaigns);

      const newPlaylist = apiService.generatePlaylist(activeCampaigns);
      setPlaylist(newPlaylist);

      if (newPlaylist.length > 0) {
        setCurrentCampaign(newPlaylist[0]);
        preloadNextCampaign(1, newPlaylist);
      }

      setLastGetRefreshTime(new Date());
    }
  }, [data]);

  // Wake lock management
  useEffect(() => {
    requestWakeLock();

    const wakeLockInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    }, 30000);

    return () => {
      clearInterval(wakeLockInterval);
      releaseWakeLock();
    };
  }, []);

  // WebSocket connection (optional - keep if needed)
  useEffect(() => {
    if (deviceInfo?.deviceId) {
      webSocketService.connect(deviceInfo.deviceId);

      const handleWsMessage = (event) => {
        const { topic, data } = event.detail;
        if (data.type === 'CAMPAIGN_UPDATE' || data.campaignId) {
          handleCampaignUpdate(data);
        }
      };

      window.addEventListener('ws-message', handleWsMessage);

      return () => {
        window.removeEventListener('ws-message', handleWsMessage);
        webSocketService.disconnect();
      };
    }
  }, [deviceInfo?.deviceId]);

  const handleCampaignUpdate = (updatedCampaign) => {
    //console.log('📩 WebSocket update received');
    setCampaigns(prevCampaigns => {
      const newCampaigns = prevCampaigns.map(c =>
        c.campaignId === updatedCampaign.campaignId
          ? { ...c, ...updatedCampaign }
          : c
      );

      const newPlaylist = apiService.generatePlaylist(newCampaigns);
      setPlaylist(newPlaylist);

      return newCampaigns;
    });

    if (currentCampaign && currentCampaign.campaignId === updatedCampaign.campaignId) {
      setCurrentCampaign(prev => ({ ...prev, ...updatedCampaign }));
    }
  };

  // Preload next campaign
  const preloadNextCampaign = (startIndex, currentPlaylist = playlist) => {
    if (currentPlaylist.length === 0) return;

    const nextIndex = (startIndex) % currentPlaylist.length;
    const next = currentPlaylist[nextIndex];

    if (next) {
      setNextCampaign(next);

      if (next.advertisementType === 'IMAGE') {
        const img = new Image();
        img.src = next.imageUrl;
      } else {
        if (nextVideoRef.current) {
          nextVideoRef.current.src = next.imageUrl;
          nextVideoRef.current.load();
        }
      }
    }
  };

  // Mouse move handler for back button
  useEffect(() => {
    const handleMouseMove = () => {
      setShowBackButton(true);
      if (hideButtonTimerRef.current) {
        clearTimeout(hideButtonTimerRef.current);
      }
      hideButtonTimerRef.current = setTimeout(() => {
        setShowBackButton(false);
      }, 5000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (hideButtonTimerRef.current) {
        clearTimeout(hideButtonTimerRef.current);
      }
    };
  }, []);

  const activateCampaign = async (campaign) => {
    if (!campaign) return;

    try {
      setCurrentCampaign(campaign);

      const nextIndex = (playlist.findIndex(c => c.campaignId === campaign.campaignId) + 1) % playlist.length;
      preloadNextCampaign(nextIndex);

    } catch (error) {
      console.error('Error activating campaign:', error);
    }
  };

  const playNext = () => {
    if (playlist.length === 0) return;

    if (nextCampaign) {
      activateCampaign(nextCampaign);
    } else {
      const nextIndex = (currentIndex + 1) % playlist.length;
      activateCampaign(playlist[nextIndex]);
    }
  };

  const startPlaybackTimer = (duration) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (preloadTimerRef.current) {
      clearTimeout(preloadTimerRef.current);
    }

    const preloadTime = Math.max(duration * 0.7, 1000);
    preloadTimerRef.current = setTimeout(() => {
      const nextIndex = (playlist.findIndex(c => c.campaignId === currentCampaign?.campaignId) + 1) % playlist.length;
      preloadNextCampaign(nextIndex);
    }, preloadTime);

    timerRef.current = setTimeout(() => {
      playNext();
    }, duration);
  };

  const handleVideoEnd = () => {
    playNext();
  };

  const handleBackClick = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
    if (hideButtonTimerRef.current) clearTimeout(hideButtonTimerRef.current);
    if (getIntervalRef.current) clearInterval(getIntervalRef.current);
    if (putIntervalRef.current) clearInterval(putIntervalRef.current);
    releaseWakeLock();
    webSocketService.disconnect();
    onLogout();
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (preloadTimerRef.current) clearTimeout(preloadTimerRef.current);
      if (getIntervalRef.current) clearInterval(getIntervalRef.current);
      if (putIntervalRef.current) clearInterval(putIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (playlist.length > 0 && currentCampaign) {
      const index = playlist.findIndex(c => c.campaignId === currentCampaign.campaignId);
      if (index !== -1) setCurrentIndex(index);
    }
  }, [playlist, currentCampaign]);

  if (!currentCampaign) {
    return (
      <div className="promotions-deta">
        <div className="no-campaigns">
          <h2>No Active Campaigns</h2>
          <p>No digital campaigns available to display</p>
          <button onClick={handleBackClick} className="back-btn">Back to Login</button>
        </div>

      </div>
    );
  }

  return (
    <div className="promotions-deta">
      <video
        ref={nextVideoRef}
        style={{ display: 'none' }}
        muted
        preload="auto"
      />

      <button
        className={`back-button ${showBackButton ? 'visible' : ''}`}
        onClick={handleBackClick}
        aria-label="Go back to login"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className="display-area">
        <div className="media-player">
          {currentCampaign.advertisementType === 'IMAGE' ? (
            <img
              key={currentCampaign.campaignId}
              src={currentCampaign.imageUrl}
              alt={currentCampaign.campaignName}
              className="campaign-media"
              onLoad={() => {
                startPlaybackTimer(currentCampaign.durationSeconds * 1000);
              }}
            />
          ) : (
            <video
              key={currentCampaign.campaignId}
              ref={videoRef}
              src={currentCampaign.imageUrl}
              className="campaign-media"
              autoPlay
              onEnded={handleVideoEnd}
              onLoadedMetadata={() => {
                startPlaybackTimer(currentCampaign.durationSeconds * 1000);
              }}
            />
          )}
        </div>
      </div>


    </div>
  );
};

export default PromotionDeta;